import { useState, useRef, useEffect } from 'react';
import { useLang } from '@/context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot } from 'lucide-react';

const faqData = {
  en: [
    { q: 'services', a: 'We offer Web Design, Web Development, Branding, and UI/UX Design. Each service is tailored to your brand\'s unique needs.' },
    { q: 'pricing', a: 'Our projects typically start from $5,000 for branding packages and $10,000+ for web design & development. We provide custom quotes based on project scope.' },
    { q: 'timeline', a: 'Most projects take 4-8 weeks from kickoff to launch. Complex platforms may take longer. We\'ll provide a detailed timeline in our proposal.' },
    { q: 'process', a: 'Our process: Discovery & Strategy, Design Concepts, Development, Testing & QA, Launch. We keep you involved at every step.' },
    { q: 'contact', a: 'You can reach us through our contact form or scroll down directly and complete. We will respond within 24 hours.' },
    { q: 'technologies', a: 'We work with React, Next.js, Figma, Tailwind CSS, Node.js, and more. We choose the best stack for each project.' },
  ],
  fr: [
    { q: 'services', a: 'Nous proposons Web Design, Developpement Web, Branding et Design UI/UX. Chaque service est adapte aux besoins uniques de votre marque.' },
    { q: 'pricing', a: 'Nos projets commencent generalement a partir de 5 000 EUR pour les packages branding et 10 000+ EUR pour le web design & developpement.' },
    { q: 'timeline', a: 'La plupart des projets prennent 4 a 8 semaines du lancement a la livraison. Nous fournissons un calendrier detaille dans notre proposition.' },
    { q: 'process', a: 'Notre processus : Decouverte & Strategie, Concepts Design, Developpement, Tests & QA, Lancement. Nous vous impliquons a chaque etape.' },
    { q: 'contact', a: 'Vous pouvez nous contacter via le formulaire sur cette page. Descendez pour le remplir. Nous repondons sous 24 heures !' },
    { q: 'technologies', a: 'Nous travaillons avec React, Next.js, Figma, Tailwind CSS, Node.js et plus encore. Nous choisissons la meilleure stack pour chaque projet.' },
  ],
};

const suggestions = {
  en: ['What services do you offer?', 'What are your prices?', 'How long does a project take?', 'What is your process?', 'How can I contact you?', 'What technologies do you use?'],
  fr: ['Quels services proposez-vous ?', 'Quels sont vos tarifs ?', 'Combien de temps dure un projet ?', 'Quel est votre processus ?', 'Comment vous contacter ?', 'Quelles technologies utilisez-vous ?'],
};

const greetings = {
  en: 'Hi! I\'m the MadisonFred assistant. How can I help you today? Choose a question below or type your own.',
  fr: 'Bonjour ! Je suis l\'assistant MadisonFred. Comment puis-je vous aider ? Choisissez une question ou ecrivez la votre.',
};

const keywordMap = {
  en: {
    services: ['service', 'offer', 'do', 'help', 'provide'],
    pricing: ['price', 'cost', 'budget', 'much', 'rate', 'fee', 'pricing'],
    timeline: ['time', 'long', 'duration', 'week', 'month', 'deadline', 'timeline'],
    process: ['process', 'work', 'step', 'workflow', 'approach', 'method'],
    contact: ['contact', 'reach', 'email', 'phone', 'call', 'touch'],
    technologies: ['tech', 'stack', 'tool', 'framework', 'language', 'platform'],
  },
  fr: {
    services: ['service', 'proposez', 'offrez', 'faites'],
    pricing: ['prix', 'cout', 'tarif', 'budget', 'combien'],
    timeline: ['temps', 'duree', 'semaine', 'mois', 'delai', 'combien'],
    process: ['processus', 'methode', 'etape', 'fonctionnez', 'approche'],
    contact: ['contact', 'joindre', 'email', 'telephone', 'appeler'],
    technologies: ['tech', 'stack', 'outil', 'framework', 'technologie'],
  },
};

function findAnswer(input, lang) {
  const lower = input.toLowerCase();
  const keywords = keywordMap[lang];
  const faqs = faqData[lang];
  for (const [topic, words] of Object.entries(keywords)) {
    if (words.some(w => lower.includes(w))) {
      const faq = faqs.find(f => f.q === topic);
      if (faq) return faq.a;
    }
  }
  return lang === 'en'
    ? 'I\'m not sure about that. Try asking about our services, pricing, timeline, process, or technologies. Or use the contact form to reach our team directly!'
    : 'Je ne suis pas sur de comprendre. Essayez de demander nos services, tarifs, delais, processus ou technologies. Ou utilisez le formulaire de contact !';
}

export default function ChatWidget() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: 'bot', text: greetings[lang] }]);
    }
  }, [open, lang, messages.length]);

  // Reset chat when language changes
  useEffect(() => {
    setMessages([]);
  }, [lang]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setInput('');
    setTimeout(() => {
      const answer = findAnswer(userMsg, lang);
      setMessages(prev => [...prev, { from: 'bot', text: answer }]);
    }, 500);
  };

  return (
    <>
      {/* Toggle button */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-20 right-6 z-[60] bg-white text-black w-14 h-14 flex items-center justify-center hover:bg-zinc-200 transition-colors shadow-2xl"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            data-testid="chat-panel"
            className="fixed bottom-36 right-6 z-[60] w-[360px] max-h-[500px] bg-zinc-900 border border-white/10 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
              <Bot size={20} className="text-zinc-400" />
              <div>
                <p className="font-outfit font-semibold text-sm text-zinc-50">MadisonFred</p>
                <p className="text-xs text-zinc-500">
                  {lang === 'en' ? 'Online — Ask us anything' : 'En ligne — Posez-nous vos questions'}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[300px]">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    data-testid={`chat-msg-${i}`}
                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                      msg.from === 'user'
                        ? 'bg-white text-black'
                        : 'bg-zinc-800 text-zinc-300 border border-white/5'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />

              {/* Suggestions (only after greeting) */}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {suggestions[lang].map((s, i) => (
                    <button
                      key={i}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => handleSend(s)}
                      className="text-xs px-3 py-1.5 border border-white/10 text-zinc-400 hover:text-zinc-50 hover:border-white/30 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="px-4 py-3 border-t border-white/5 flex gap-2"
            >
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={lang === 'en' ? 'Type a message...' : 'Ecrivez un message...'}
                className="flex-1 bg-transparent text-zinc-50 text-sm placeholder:text-zinc-600 outline-none"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                className="text-zinc-400 hover:text-zinc-50 transition-colors"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
